import { useState, useEffect, useRef } from 'react'
import { Slider } from 'src/360ui/Slider'
import Image from 'next/image'
import styles from './styles.module.scss'
import { updateUrlWithArrayParamsMinMax } from 'src/utils/url'
import { formatCurrency2CompactString } from 'src/utils/number/currency'
import useWidth from 'src/hooks/useWidth'
import clsx from 'clsx'
import { LogarithmicRange } from 'src/components/LogarithmicRange'
import { intl } from 'src/i18n'

export interface RangeFiltersProps {
  min?: { [key: string]: any }
  max?: { [key: string]: any }
  defaultVolumeMin?: number
  defaultVolumeMax?: number
  defaultMarketCapMin?: number
  defaultMarketCapMax?: number
  defaultFloorPriceRangeMin?: number
  defaultFloorPriceRangeMax?: number
  defaultNumberOfOwnersMin?: number
  defaultNumberOfOwnersMax?: number
  defaultNumberOfAssetsMin?: number
  defaultNumberOfAssetsMax?: number
}

export const RangeFilters = ({
  min,
  max,
  defaultVolumeMin,
  defaultVolumeMax,
  defaultMarketCapMin,
  defaultMarketCapMax,
  defaultFloorPriceRangeMin,
  defaultFloorPriceRangeMax,
  defaultNumberOfOwnersMin,
  defaultNumberOfOwnersMax,
  defaultNumberOfAssetsMin,
  defaultNumberOfAssetsMax,
}: RangeFiltersProps) => {
  const { isMobile } = useWidth()

  const [volume, setVolume] = useState<number[]>([
    defaultVolumeMin,
    defaultVolumeMax,
  ])

  // const [marketCap, setMarketCap] = useState<number[]>([
  //   defaultMarketCapMin,
  //   defaultMarketCapMax,
  // ])
  const [floorPrice, setFloorPrice] = useState<number[]>([
    defaultFloorPriceRangeMin,
    defaultFloorPriceRangeMax,
  ])
  const [numberOfOwners, setNumberOfOwners] = useState<number[]>([
    defaultNumberOfOwnersMin,
    defaultNumberOfOwnersMax,
  ])
  const [numberOfassets, setNumberOfassets] = useState<number[]>([
    defaultNumberOfAssetsMin,
    defaultNumberOfAssetsMax,
  ])

  const [edit, setEdit] = useState<boolean>(false)
  const showContent = !isMobile || (edit && isMobile)
  const [showContainer, setShowContainer] = useState<boolean>(isMobile)
  const refButton = useRef(null)

  useEffect(() => {
    setShowContainer(isMobile)
  }, [isMobile])

  useEffect(() => {
    const handleClickOutside = event => {
      if (refButton.current && !refButton.current.contains(event.target)) {
        setShowContainer(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [refButton, showContainer])

  const params = [
    {
      name: intl.t('nft.filters.rangeFilter.volume'),
      param: 'volume',
      min: 'filter_volume_min',
      max: 'filter_volume_max',
      value: volume,
    },
    // {
    //   name: 'Market Cap',
    //   param: 'marketCap',
    //   min: 'filter_market_cap_min',
    //   max: 'filter_market_cap_max',
    //   value: marketCap,
    // },
    {
      name: intl.t('nft.filters.rangeFilter.floorPrice'),
      param: 'floorPriceRange',
      min: 'filter_floor_price_min',
      max: 'filter_floor_price_max',
      value: floorPrice,
    },
    {
      name: intl.t('nft.filters.rangeFilter.numberOfOwners'),
      param: 'numberOfOwners',
      min: 'filter_number_of_owners_min',
      max: 'filter_number_of_owners_max',
      value: numberOfOwners,
    },
    {
      name: intl.t('nft.filters.rangeFilter.numberOfAssets'),
      param: 'numberOfItems',
      min: 'filter_number_of_items_min',
      max: 'filter_number_of_items_max',
      value: numberOfassets,
    },
  ]

  const handleApplyFilter = e => {
    e.preventDefault()
    e.stopPropagation()

    const paramsChecked = []

    params.map(param => {
      switch (param.param) {
        case 'volume':
          if (
            param.value[0] !== defaultVolumeMin ||
            param.value[1] !== defaultVolumeMax
          ) {
            paramsChecked.push(param)
          }
          break
        case 'marketCap':
          if (
            param.value[0] !== defaultMarketCapMin ||
            param.value[1] !== defaultMarketCapMax
          ) {
            paramsChecked.push(param)
          }
          break
        case 'floorPriceRange':
          if (
            param.value[0] !== defaultFloorPriceRangeMin ||
            param.value[1] !== defaultFloorPriceRangeMax
          ) {
            paramsChecked.push(param)
          }
          break
        case 'numberOfOwners':
          if (
            param.value[0] !== defaultNumberOfOwnersMin ||
            param.value[1] !== defaultNumberOfOwnersMax
          ) {
            paramsChecked.push(param)
          }
          break
        case 'numberOfItems':
          if (
            param.value[0] !== defaultNumberOfAssetsMin ||
            param.value[1] !== defaultNumberOfAssetsMax
          ) {
            paramsChecked.push(param)
          }
          break
        default:
          break
      }
    })

    if (paramsChecked.length > 0) {
      updateUrlWithArrayParamsMinMax(paramsChecked)
    }

    if (isMobile) {
      setEdit(false)
    } else {
      setShowContainer(false)
    }
  }

  const handleResetFilter = () => {
    setVolume([0, max.volume])
    setFloorPrice([0, max.floorPrice])
    // setMarketCap([0, max.marketCap])
    setNumberOfOwners([1, max.numberOfOwners])
    setNumberOfassets([1, max.numberOfItems])
  }

  return (
    <div
      className={styles.rangeButton}
      onClick={!isMobile ? () => setShowContainer(true) : null}
      ref={refButton}
    >
      {!isMobile && <span>Range</span>}
      {showContainer && (
        <div
          className={clsx(styles.rangeContainer, {
            [styles.filtersMobile]: edit,
          })}
        >
          {isMobile && !edit && (
            <div className={styles.mobileContainer}>
              <div className={styles.header}>
                <span>{intl.t('nft.filters.rangeFilter.range')}</span>
                <div className={styles.headerButtons}>
                  <div
                    className={styles.iconEdit}
                    onClick={() => setEdit(true)}
                  >
                    <Image
                      src='/assets/icons/icon-edit.svg'
                      width={17}
                      height={18}
                      alt='Edit'
                    />
                  </div>
                </div>
              </div>
              {params.map(({ name, value }) => (
                <div key={name} className={styles.paramInfo}>
                  <div className={styles.paramName}>{name}:</div>
                  <div className={styles.paramValue}>
                    {formatCurrency2CompactString(value[0])} -{' '}
                    {formatCurrency2CompactString(value[1])}
                  </div>
                </div>
              ))}
            </div>
          )}

          {showContent && (
            <>
              {isMobile && (
                <div className={styles.buttonsContainer}>
                  <span className={styles.spanTitle}>Range</span>
                  <div className={styles.buttons}>
                    <div
                      className={clsx(styles.reset, styles.mobileButton)}
                      onClick={() => setEdit(false)}
                    >
                      {intl.t('nft.filters.rangeFilter.cancel')}
                    </div>
                    <div
                      className={clsx(
                        styles.apply,
                        styles.mobileButton,
                        styles.applyMobile
                      )}
                      onClick={handleApplyFilter}
                    >
                      {intl.t('nft.filters.rangeFilter.save')}
                    </div>
                  </div>
                </div>
              )}

              <Slider
                label={'Volume'}
                minValue={min.volume}
                maxValue={max.volume}
                value={volume}
                onChange={value => setVolume(value as number[])}
                onReset={() => {
                  setVolume([0, max.volume])
                }}
              />
              <Slider
                label={'Floor price'}
                minValue={0}
                maxValue={max.floorPrice}
                value={floorPrice}
                onChange={value => setFloorPrice(value as number[])}
                onReset={() => {
                  setFloorPrice([0, max.floorPrice])
                }}
              />

              <LogarithmicRange
                label={'Number of owners'}
                maxValue={max.numberOfOwners}
                value={numberOfOwners}
                action={setNumberOfOwners}
                className={undefined}
              />

              <LogarithmicRange
                label={'Number of assets'}
                maxValue={max.numberOfItems}
                value={numberOfassets}
                action={setNumberOfassets}
                className={undefined}
              />
              {/*TODO: update buttons with button aria*/}
              {!isMobile && (
                <div className={styles.buttonsContainer}>
                  <div
                    className={styles.apply}
                    onClick={(event: any) => handleApplyFilter(event)}
                  >
                    {intl.t('nft.filters.rangeFilter.ok')}
                  </div>
                  <div className={styles.reset} onClick={handleResetFilter}>
                    {intl.t('nft.filters.rangeFilter.resetFilters')}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
